import { useCallbackEffect, useLoggingCallback } from '@databases/react';
import {
  BlogpostReference,
  BlogpostReferenceOrContentId,
  Content,
  CreateOrResolvedPageReference,
  OptionalCreatePageReference,
  OptionalCreatePageReferenceOrContentId,
  RequiredCreatePageReference,
} from '@databases/types/confluence';
import {
  blogpostToReference,
  contentToReference,
  isBlogpostReference,
  isContent,
  isPageReference,
  pageToReference,
} from '@databases/utils/confluence/utils';
import { loadContentById, loadContentRef } from '@databases/utils/redux/content/actions';
import {
  selectContentByIdOptional,
  selectContentRefOptional,
} from '@databases/utils/redux/content/selectors';

import { defaultStoreUtils } from './redux';

const isCreatePage = (
  ref: OptionalCreatePageReference
): ref is RequiredCreatePageReference => {
  return 'create' in ref && !!ref.create;
};

export const useResolvedReference = (
  contentRef:
    | OptionalCreatePageReferenceOrContentId
    | BlogpostReferenceOrContentId
    | Content<'page'>
    | Content<'blogpost'>
    | undefined
):
  | CreateOrResolvedPageReference
  | BlogpostReference
  | Content<'page'>
  | Content<'blogpost'>
  | undefined => {
  const dispatch = defaultStoreUtils.useDispatch();
  useCallbackEffect(
    useLoggingCallback(async () => {
      if (!contentRef) {
        return;
      }

      if ('contentId' in contentRef && contentRef.contentId) {
        await dispatch(loadContentById(contentRef.contentId));
      } else if (
        !('create' in contentRef) &&
        (isPageReference(contentRef) || isBlogpostReference(contentRef))
      ) {
        await dispatch(loadContentRef(contentRef));
      }
    }, [dispatch, contentRef])
  );

  const resolvedContentRef:
    | CreateOrResolvedPageReference
    | BlogpostReference
    | Content<'page'>
    | Content<'blogpost'>
    | undefined = defaultStoreUtils.useEqualSelector((state) => {
    if (!contentRef) {
      return undefined;
    }
    if ('create' in contentRef && isCreatePage(contentRef)) {
      return contentRef;
    }

    if ('contentId' in contentRef) {
      const resolvedRef = selectContentByIdOptional(state, {
        contentId: contentRef.contentId,
      });
      if (resolvedRef) {
        if (
          isContent(resolvedRef) &&
          (resolvedRef.type === 'page' || resolvedRef.type === 'blogpost')
        ) {
          return contentToReference(resolvedRef);
        }
      }
    } else {
      if (isContent(contentRef)) {
        if (contentRef.type === 'page') {
          return pageToReference(contentRef);
        } else if (contentRef.type === 'blogpost') {
          return blogpostToReference(contentRef);
        }
      } else {
        return selectContentRefOptional(state, { contentRef });
      }
    }
  });

  return resolvedContentRef;
};
